import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Menu } from '@systemeio/components'
import { ChevronDownIcon } from '@systemeio/icons'
import { cn, twJoin } from 'cn'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { useWorkspaceSelect } from './api/use-workspace-select'
import { useLogoUrl } from './hooks/use-logo-url'

interface WorkspaceSelectorProps {
  className?: string
  position: 'top-menu' | 'side-menu'
}
interface WorkspaceInterface {
  id: number | null
  isWorkspace: boolean
  name: string
  avatarUrl: string
}

const triggerClassName =
  'border-field-border text-text-primary data-popup-open:border-field-border-active data-popup-open:[&>svg]:rotate-180 transition-base flex w-full min-w-0 items-center justify-between gap-1.5 rounded-md border px-3 py-1.5 text-base font-medium outline-none data-popup-open:bg-bg-tertiary'

export function WorkspaceSelector({ position, className }: WorkspaceSelectorProps): ReactNode {
  const { user, isOwner } = useUser()
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceInterface>()
  const [workspaces, setWorkspaces] = useState<WorkspaceInterface[]>([])
  const redirectUrl = useLogoUrl()
  const workspaceSelectMutation = useWorkspaceSelect()

  useEffect(() => {
    if (isAuthorizedUser(user) && user.workspaceMemberships.length > 0) {
      setWorkspaces([
        ...user.workspaceMemberships.map(el => ({
          isWorkspace: el.id === selectedWorkspace?.id,
          id: el.id,
          name: el.name,
          avatarUrl: el.image,
        })),
        {
          id: user.workspaceMemberships[0].ownerId,
          isWorkspace: false,
          name: user.subdomain,
          avatarUrl: user.avatarUrl,
        },
      ])
    }
  }, [user, isOwner])

  useEffect(() => {
    if (user && isAuthorizedUser(user) && workspaces.length > 0) {
      if (user.workspaceOwner) {
        setSelectedWorkspace(_prev => {
          const el = user.workspaceMemberships.find(el => el.ownerId === user.workspaceOwner)
          if (el) {
            return {
              name: el.name,
              isWorkspace: true,
              id: el.id,
              avatarUrl: el.image,
            }
          }
        })
      } else {
        setSelectedWorkspace({
          name: user.subdomain,
          isWorkspace: false,
          id: user.workspaceMemberships[0].ownerId,
          avatarUrl: user.avatarUrl,
        })
      }
    }
  }, [workspaces])

  const onWorkspaceChange = (workspace: WorkspaceInterface): void => {
    if (user && isAuthorizedUser(user) && redirectUrl) {
      const isOwner = workspace.id === user.workspaceMemberships[0].ownerId
      workspaceSelectMutation.mutate({ currentWorkspaceMembership: isOwner ? null : workspace.id })
    }
  }

  if (!selectedWorkspace) {
    return null
  }

  const otherWorkspaces = workspaces.filter(el => el.id !== selectedWorkspace.id)
  const wrapperClassName = twJoin(position === 'top-menu' && 'hidden lg:flex', className || '')

  if (otherWorkspaces.length === 0) {
    return (
      <div className={wrapperClassName}>
        {position === 'side-menu' && (
          <img
            className="size-7 rounded-full object-cover"
            src={selectedWorkspace.avatarUrl}
            alt={`${selectedWorkspace.name}`}
          />
        )}
        <span className="truncate font-bold">{selectedWorkspace.name}</span>
      </div>
    )
  }

  return (
    <div className={wrapperClassName}>
      <Menu
        disabled={workspaceSelectMutation.isPending}
        className="tablet:max-h-79 tablet:w-fit tablet:max-w-full tablet:min-w-(--anchor-width)"
        trigger={[
          position === 'side-menu' && (
            <img
              key="workspace-avatar"
              className="size-7 rounded-full object-cover"
              src={selectedWorkspace.avatarUrl}
              alt={`${selectedWorkspace.name}`}
            />
          ),
          <span key="workspace-name" className="min-w-0 flex-1 truncate text-start">
            {selectedWorkspace.name}
          </span>,
          <ChevronDownIcon
            key="workspace-chevron"
            className="size-3 shrink-0 self-center"
            aria-hidden="true"
          />,
        ]}
        triggerProps={{
          className: cn(triggerClassName, workspaceSelectMutation.isPending && 'animate-pulse'),
        }}
        items={otherWorkspaces.map(el => ({
          id: String(el.id),
          label: (
            <span className="flex min-w-0 items-center gap-3">
              <img
                className="size-7 rounded-full object-cover"
                src={el.avatarUrl}
                alt={`${el.name}`}
              />
              <span className="truncate">{el.name}</span>
            </span>
          ),
          onSelect: () => onWorkspaceChange(el),
        }))}
      />
    </div>
  )
}
